import { useState, useMemo, useCallback } from 'react';
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useGmbReviews, useGmbReviewsOverview, useGenerateReviewResponse, useMarkReviewResponded } from '../../hooks/useGmbImplementation';

interface ReviewManagementProps {
  onTooltipShow: (title: string, content: string) => void;
}

function sentimentFromRating(rating: number): 'Positive' | 'Neutral' | 'Negative' {
  if (rating >= 4) return 'Positive';
  if (rating === 3) return 'Neutral';
  return 'Negative';
}

function sentimentColor(rating: number): string {
  if (rating >= 4) return 'text-green-600 dark:text-green-400';
  if (rating === 3) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function sentimentBg(rating: number): string {
  if (rating >= 4) return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
  if (rating === 3) return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
  return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
}

function StarRating({ rating, size = 'text-xs' }: { rating: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`${size} ${s <= rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300 dark:text-gray-600'}`}
        />
      ))}
    </div>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ReviewManagement({ onTooltipShow }: ReviewManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'priority' | 'newest' | 'oldest' | 'highest' | 'lowest'>('priority');
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [editedResponse, setEditedResponse] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: reviewsData, isLoading: reviewsLoading, error: reviewsError, refetch: refetchReviews } = useGmbReviews();
  const { data: overviewData, isLoading: overviewLoading } = useGmbReviewsOverview();
  const generateResponseMutation = useGenerateReviewResponse();
  const markRespondedMutation = useMarkReviewResponded();

  const allReviews = reviewsData?.data?.reviews || [];
  const overview = overviewData?.data || {};

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const filteredReviews = useMemo(() => {
    let result = [...allReviews];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r: any) =>
          r.reviewer_name.toLowerCase().includes(q) ||
          r.review_text.toLowerCase().includes(q)
      );
    }

    if (ratingFilter !== 'All') {
      result = result.filter((r: any) => String(r.rating) === ratingFilter);
    }

    if (statusFilter !== 'All') {
      const statusMap: Record<string, string> = {
        'Responded': 'responded',
        'Response Pending': 'pending',
      };
      result = result.filter((r: any) => r.response_status === statusMap[statusFilter]);
    }

    if (sortBy === 'priority') {
      result.sort((a: any, b: any) => {
        const aPending = a.response_status === 'pending' ? 1 : 0;
        const bPending = b.response_status === 'pending' ? 1 : 0;
        if (aPending !== bPending) return bPending - aPending;
        if (aPending === 1) return a.rating - b.rating;
        return new Date(b.review_date).getTime() - new Date(a.review_date).getTime();
      });
    } else if (sortBy === 'newest') {
      result.sort((a: any, b: any) => new Date(b.review_date).getTime() - new Date(a.review_date).getTime());
    } else if (sortBy === 'oldest') {
      result.sort((a: any, b: any) => new Date(a.review_date).getTime() - new Date(b.review_date).getTime());
    } else if (sortBy === 'highest') {
      result.sort((a: any, b: any) => b.rating - a.rating);
    } else if (sortBy === 'lowest') {
      result.sort((a: any, b: any) => a.rating - b.rating);
    }

    return result;
  }, [allReviews, searchQuery, ratingFilter, statusFilter, sortBy]);

  const selectedReview = allReviews.find((r: any) => r.id === selectedReviewId) || null;

  const handleGenerateResponse = async () => {
    if (!selectedReview || selectedReview.response_status === 'responded') return;
    try {
      const result = await generateResponseMutation.mutateAsync(selectedReview.id);
      const response = result?.data?.ai_suggested_response || result?.ai_suggested_response || '';
      setEditedResponse(response);
    } catch (err: any) {
      showToast(err.message || 'Failed to generate response');
    }
  };

  const handleCopyResponse = async () => {
    if (!editedResponse) return;
    try {
      await navigator.clipboard.writeText(editedResponse);
      setCopiedId(selectedReviewId);
      showToast('Response copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showToast('Failed to copy');
    }
  };

  const handleMarkResponded = async () => {
    if (!selectedReviewId || !editedResponse) return;
    try {
      await markRespondedMutation.mutateAsync({ reviewId: selectedReviewId, responseText: editedResponse });
      setSelectedReviewId(null);
      setEditedResponse('');
      showToast('Marked as Response Posted');
    } catch (err: any) {
      showToast(err.message || 'Failed to mark as responded');
    }
  };

  const handleClearSelection = () => {
    setSelectedReviewId(null);
    setEditedResponse('');
  };

  if (reviewsError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">Failed to load reviews</h3>
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{reviewsError.message}</p>
        <button onClick={() => refetchReviews()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (reviewsLoading || overviewLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-check-line text-green-400" />
          {toast}
        </div>
      )}

      {/* Review Overview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Review Overview</h3>
          <button
            onClick={() => onTooltipShow('Review Management', 'Track your review metrics, respond to customer reviews with AI-generated responses, and manage your online reputation.')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="More info"
          >
            <i className="ri-information-line text-lg"></i>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center text-gray-700 dark:text-gray-300">
                <i className="ri-star-line text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Reviews</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.total_reviews || 0}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-amber-50 dark:bg-amber-900/30 rounded-md flex items-center justify-center text-amber-600 dark:text-amber-400">
                <i className="ri-star-fill text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Rating</span>
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.avg_rating || 0}</div>
              <div className="text-xs text-gray-400 dark:text-gray-500">/ 5</div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-teal-50 dark:bg-teal-900/30 rounded-md flex items-center justify-center text-teal-600 dark:text-teal-400">
                <i className="ri-calendar-line text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">This Month</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.this_month || 0}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-green-50 dark:bg-green-900/30 rounded-md flex items-center justify-center text-green-600 dark:text-green-400">
                <i className="ri-check-double-line text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Response Status</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.responded_count || 0}</div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500">Responded</div>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-gray-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.not_responded_count || 0}</div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500">Not Responded</div>
              </div>
            </div>
            <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{ width: `${overview.response_rate_pct || 0}%` }}
              />
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{overview.response_rate_pct || 0}% response rate</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center text-gray-700 dark:text-gray-300">
                <i className="ri-bar-chart-box-line text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rating Trend</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{overview.avg_rating || 0}</div>
              <div className={`flex items-center gap-0.5 text-xs font-medium ${overview.rating_trend === 'up' ? 'text-green-600 dark:text-green-400' : overview.rating_trend === 'down' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {overview.rating_comparison_available ? (
                  <>
                    <i className={overview.rating_trend === 'up' ? 'ri-arrow-up-line' : overview.rating_trend === 'down' ? 'ri-arrow-down-line' : 'ri-arrow-right-line'} />
                    {overview.rating_change || 0} vs last period
                  </>
                ) : (
                  <>
                    <i className="ri-information-line" />
                    No recent review data
                  </>
                )}
              </div>
            </div>
            {overview.rating_trend_label && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{overview.rating_trend_label}</p>
            )}
          </div>
        </div>
      </div>

      {/* Review Feed */}
      <div>
        <div className="w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Review Feed</h3>
          <span className="text-xs text-gray-400 dark:text-gray-500">{filteredReviews.length} of {allReviews.length} reviews shown</span>
        </div>

        {/* Filters bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm space-y-3 mb-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer name or review text..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">Rating:</label>
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="All">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="All">All Status</option>
                <option value="Response Pending">Response Pending</option>
                <option value="Responded">Responded</option>
              </select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="priority">Priority (Needs Attention)</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="highest">Highest Rating</option>
                <option value="lowest">Lowest Rating</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reviews table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Customer</th>
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Rating</th>
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Review</th>
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Date</th>
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Status</th>
                  <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2.5 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredReviews.map((review: any) => {
                  const isSelected = selectedReviewId === review.id;
                  return (
                    <tr
                      key={review.id}
                      onClick={() => {
                        if (review.response_status === 'pending') {
                          if (selectedReviewId === review.id) {
                            handleClearSelection();
                            return;
                          }
                          setSelectedReviewId(review.id);
                          setEditedResponse(review.ai_suggested_response || '');
                        }
                      }}
                      className={`border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors cursor-pointer ${
                        isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-750'
                      } ${review.response_status === 'pending' ? '' : 'cursor-default'}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 text-[10px] font-bold">
                            {review.reviewer_name.split(' ').map((n: string) => n[0]).join('')}
                          </div>
                          <span className="text-gray-800 dark:text-gray-200 font-medium">{review.reviewer_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <StarRating rating={review.rating} />
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${sentimentBg(review.rating)} ${sentimentColor(review.rating)}`}>
                            {sentimentFromRating(review.rating)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-gray-700 dark:text-gray-300 max-w-xs truncate" title={review.review_text}>{review.review_text}</p>
                      </td>
                      <td className="py-3 px-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(review.review_date)}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            review.response_status === 'responded'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          }`}
                        >
                          {review.response_status === 'responded' ? (
                            <>
                              <i className="ri-check-line" />
                              Responded
                            </>
                          ) : (
                            <>
                              <i className="ri-time-line" />
                              Response Pending
                            </>
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {review.response_status === 'pending' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReviewId(review.id);
                              setEditedResponse('');
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-sparkling-line" />
                            Assist
                          </button>
                        ) : (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredReviews.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">
                      <i className="ri-search-line text-xl text-gray-300 dark:text-gray-600 mb-2 block" />
                      No reviews match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      {/* Review Response Assistance */}
      <Sheet
        open={selectedReview !== null && selectedReview.response_status === 'pending'}
        onOpenChange={(open) => {
          if (!open) handleClearSelection();
        }}
      >
        <SheetContent side="right" overlayClassName="bg-black/30" className="w-full sm:max-w-[380px] p-6 overflow-y-auto">
          <SheetTitle className="sr-only">Review Response Assistance</SheetTitle>
          {selectedReview && selectedReview.response_status === 'pending' && (
            <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-md flex items-center justify-center text-violet-600 dark:text-violet-400">
                <i className="ri-sparkling-line text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Review Response Assistance</h3>
                <p className="text-[10px] text-gray-400 dark:text-gray-500">AI-powered response generation based on review sentiment</p>
              </div>
            </div>
          </div>

          {/* Selected review preview */}
          <div className={`rounded-lg border p-4 ${sentimentBg(selectedReview.rating)}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 text-[10px] font-bold">
                {selectedReview.reviewer_name.split(' ').map((n: string) => n[0]).join('')}
              </div>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{selectedReview.reviewer_name}</span>
              <StarRating rating={selectedReview.rating} size="text-[10px]" />
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ml-auto ${sentimentBg(selectedReview.rating)} ${sentimentColor(selectedReview.rating)}`}>
                {sentimentFromRating(selectedReview.rating)} Review
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 italic">&ldquo;{selectedReview.review_text}&rdquo;</p>
          </div>

          {/* Generate button */}
          <div className="text-center py-3">
            <button
              onClick={handleGenerateResponse}
              disabled={generateResponseMutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              {generateResponseMutation.isPending ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Generating response...
                </>
              ) : (
                <>
                  <i className="ri-sparkling-line" />
                  Generate Suggested Response
                </>
              )}
            </button>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Tone and style will auto-adjust based on review sentiment</p>
          </div>

          {/* Response editor — always available, so responses can be written manually */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <i className="ri-chat-3-line text-violet-500 text-sm" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Your Response</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ml-auto ${sentimentBg(selectedReview.rating)} ${sentimentColor(selectedReview.rating)}`}>
                {sentimentFromRating(selectedReview.rating)} tone
              </span>
            </div>

            <textarea
              value={editedResponse}
              onChange={(e) => setEditedResponse(e.target.value)}
              rows={5}
              maxLength={1000}
              placeholder="Write your response, or click 'Generate Suggested Response' to draft one automatically..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none leading-relaxed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{editedResponse.length}/1000</span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleCopyResponse}
                  disabled={!editedResponse.trim()}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                    copiedId === selectedReviewId
                      ? 'bg-green-600 text-white'
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {copiedId === selectedReviewId ? (
                    <>
                      <i className="ri-check-line" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <i className="ri-file-copy-line" />
                      Copy Response
                    </>
                  )}
                </button>
                <button
                  onClick={handleMarkResponded}
                  disabled={markRespondedMutation.isPending || !editedResponse.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-check-line" />
                  {markRespondedMutation.isPending ? 'Saving...' : 'Mark as Posted'}
                </button>
              </div>
            </div>

            <p className="text-[10px] text-gray-400 dark:text-gray-500">
              Copy this response and paste it directly into your Google Business Profile dashboard, then return here to mark it as posted.
            </p>
          </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
